import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { usePreferences } from './contexts/PreferencesContext';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Children from './pages/Children';
import Inbox from './pages/Inbox';
import Finances from './pages/Finances';
import Notifications from './pages/Notifications';
import Settings from './pages/Settings';
import LoadingScreen from './components/LoadingScreen';
import About from './pages/marketing/About';
import { IncomingCallHandler } from './components/call';
import NotificationPrompt from './components/NotificationPrompt';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

export default function App() {
  const { preferences } = usePreferences();
  const { user, loading } = useAuth();

  // Apply reduced motion class to document
  if (preferences.reducedMotion) {
    document.documentElement.classList.add('reduce-motion');
  } else {
    document.documentElement.classList.remove('reduce-motion');
  }

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className={preferences.focusMode ? 'focus-mode-active' : ''}>
      {/* Incoming call overlay for authenticated owner */}
      {user && <IncomingCallHandler />}

      {/* Notification permission prompt for authenticated users */}
      {user && <NotificationPrompt />}

      <Routes>
        {/* Marketing/Public pages */}
        <Route path="/about" element={<About />} />

        {/* Auth */}
        <Route path="/login" element={user ? <Navigate to="/app" replace /> : <Login />} />

        {/* Protected App */}
        <Route
          path="/app"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Dashboard />} />
          <Route path="children" element={<Children />} />
          <Route path="inbox" element={<Inbox />} />
          <Route path="finances" element={<Finances />} />
          <Route path="notifications" element={<Notifications />} />
          <Route path="settings" element={<Settings />} />
        </Route>

        {/* Root redirects to About page for marketing */}
        <Route path="/" element={<About />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}
